import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ClockCounterClockwise, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";
import { downloadCsv } from "@/lib/csv";

export default function Reports() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit");
      setLogs(data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

  const exportCsv = () => {
    if (!logs.length) {
      toast.error("Nothing to export");
      return;
    }
    downloadCsv("audit-logs.csv", [
      { label: "Time", value: (r) => fmt(r.created_at) },
      { label: "Actor", value: (r) => r.actor_email || "system" },
      { label: "Action", value: (r) => r.action || "" },
      { label: "Entity", value: (r) => r.entity_type || "" },
      { label: "Entity ID", value: (r) => r.entity_id || "" },
    ], logs);
  };

  return (
    <div data-testid="audit-logs-page">
      <PageHeader
        title="Audit logs"
        subtitle="Tenant, pricing, wallet and account operations recorded across the platform."
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-lg" onClick={exportCsv} disabled={!logs.length}
            data-testid="audit-export-btn">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> Export CSV
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={load} data-testid="audit-refresh-btn">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </PageHeader>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : logs.length === 0 ? (
        <EmptyState
          testid="audit-empty"
          icon={ClockCounterClockwise}
          title="No audit events yet"
          description="Tenant, pricing and wallet operations are recorded here."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="audit-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} className="hover:bg-muted/40" data-testid={`audit-row-${l.id}`}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(l.created_at)}</TableCell>
                  <TableCell className="text-sm">{l.actor_email || "system"}</TableCell>
                  <TableCell className="font-medium text-sm">{l.action}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{l.entity_type}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
